import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserRound } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, UploadPicker } from "./shared";

const EMPTY = { name: "", designation: "", intro: "", bio: "", message: "", education: "", photo: "", social: { facebook: "", instagram: "", youtube: "", whatsapp: "" }, order: 0 };

export default function FounderManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/founders").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const setS = (k) => (e) => setForm({ ...form, social: { ...form.social, [k]: e.target.value } });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/founders/${editing}`, form);
        toast.success("संस्थापक जानकारी अपडेट हुई");
      } else {
        await api.post("/admin/founders", { ...form, order: items.length });
        toast.success("संस्थापक जोड़े गए — संस्थापक पेज पर दिखने लगे");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह जानकारी हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/founders/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <button data-testid="founder-new" onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(!showForm); }} className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
        <Plus size={16} /> संस्थापक जोड़ें / जानकारी बदलें
      </button>

      {(showForm || editing) && (
        <form data-testid="founder-form" onSubmit={save} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">नाम *</label>
            <input data-testid="founder-name" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">पद</label>
            <input data-testid="founder-designation" className={inputCls} value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="सचिव" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">संक्षिप्त परिचय</label>
            <input data-testid="founder-intro" className={inputCls} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">जीवनी / योगदान</label>
            <textarea data-testid="founder-bio" rows={4} className={inputCls} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="शिक्षा, पृष्ठभूमि, संस्था स्थापना का कारण, प्रमुख कार्य..." />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">संस्थापक संदेश</label>
            <textarea data-testid="founder-message" rows={3} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">शिक्षा / पृष्ठभूमि</label>
            <input data-testid="founder-education" className={inputCls} value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">फोटो</label>
            <div className="flex gap-2 items-center">
              <input data-testid="founder-photo" className={inputCls} value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} placeholder="फोटो अपलोड करें" />
              <UploadPicker testid="founder-photo-upload" context="founder" label="अपलोड" onUploaded={(url) => setForm((p) => ({ ...p, photo: url }))} />
            </div>
            {form.photo && <img src={resolveMedia(form.photo)} alt="संस्थापक" className="mt-2 h-24 w-24 rounded-full object-cover border border-ngo-deep/10" />}
          </div>
          <div><label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">Facebook</label><input data-testid="founder-facebook" className={inputCls} value={form.social.facebook} onChange={setS("facebook")} /></div>
          <div><label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">Instagram</label><input data-testid="founder-instagram" className={inputCls} value={form.social.instagram} onChange={setS("instagram")} /></div>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="founder-save" className="px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">{editing ? "अपडेट करें" : "जोड़ें"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द करें</button>
          </div>
        </form>
      )}

      <div data-testid="founder-list" className="space-y-4">
        {items.map((f) => (
          <div key={f.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-5 flex flex-wrap items-center gap-4">
            {f.photo ? (
              <img src={resolveMedia(f.photo)} alt={f.name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-ngo-mint flex items-center justify-center text-ngo-deep"><UserRound size={24} /></div>
            )}
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-ngo-ink">{f.name}</p>
              <p className="text-xs text-ngo-ink/55 mt-0.5">{f.designation}</p>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`founder-edit-${f.id}`} onClick={() => { setEditing(f.id); setForm({ name: f.name, designation: f.designation || "", intro: f.intro || "", bio: f.bio || "", message: f.message || "", education: f.education || "", photo: f.photo || "", social: { facebook: "", instagram: "", youtube: "", whatsapp: "", ...(f.social || {}) }, order: f.order }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200"><Pencil size={15} /></button>
              <button data-testid={`founder-delete-${f.id}`} onClick={() => remove(f.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
